<template>
    <div class="plan-required" :class="{ 'plan-required--inline': inline }">
        <div v-if="inline" class="plan-req-title">{{ title }}</div>
        <template v-if="step === 1">
            <div class="plan-req-desc">{{ description }}</div>
            <div class="plan-req-row">
                <div v-for="tier in tiers" :key="tier" class="plan-req-pill" :class="{ included: isIncluded(tier) }">
                    <v-icon size="18">{{ isIncluded(tier) ? 'mdi-check' : 'mdi-lock-outline' }}</v-icon>
                    <span class="plan-req-pill-name">{{ tierLabel(tier) }}</span>
                    <span v-if="limitKey" class="plan-req-pill-limit">{{ limitForTier(tier) }}</span>
                </div>
            </div>
            <button v-if="hasTutorials" type="button" class="plan-req-tutorial" @click="openTutorials">
                <span class="plan-req-tutorial-icon">
                    <v-icon size="12">mdi-play</v-icon>
                </span>
                {{ $t('plans.watch_tutorial') }}
            </button>
            <v-btn v-if="showUpgrade" depressed tile block color="primary" class="plan-req-cta black--text"
                @click="onUpgrade">
                {{ $t('plans.upgrade') }}
            </v-btn>
        </template>
        <tutorial-list v-else ref="tutorialList" :tutorials="tutorials" :selected="selected" @select="onSelect"
            @back="back" />

        <select-plan-dialog v-if="showUpgrade" ref="selectPlan" />
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import TutorialList from '@/components/common/TutorialList'
import { TIERS, COMPANY_TIERS, LIMITS, requiredTier } from '@/utils/Plans'
import { tutorialsFrom, localizedField } from '@/utils/Tutorials'

export default {
    name: 'PlanRequiredContent',
    components: {
        TutorialList,
        SelectPlanDialog: () => import('@/components/coach/SelectPlanDialog'),
    },
    props: {
        feature: {
            type: String,
            default: null,
        },
        limitKey: {
            type: String,
            default: null,
        },
        notice: {
            type: String,
            default: null,
        },
        max: {
            type: Number,
            default: 0,
        },
        inline: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            step: 1,
            selected: null,
            fromList: false,
        }
    },
    computed: {
        ...mapState({ tutorialsConfig: 'tutorials' }),
        tiers(vm = this) {
            return vm.isCompanyPlan ? COMPANY_TIERS : TIERS
        },
        isStudent(vm = this) {
            return vm.account?.accountType === 'student'
        },
        isCompanyPlan(vm = this) {
            return ['company', 'coach_company'].includes(vm.account?.accountType)
        },
        ownsSubscription(vm = this) {
            return ['coach', 'company'].includes(vm.account?.accountType)
        },
        minTier(vm = this) {
            const required = requiredTier(vm.feature)
            if (!required || !vm.isCompanyPlan) return required
            return COMPANY_TIERS.find(tier => TIERS.indexOf(tier) >= TIERS.indexOf(required)) || null
        },
        featureLabel(vm = this) {
            if (vm.feature) return vm.$t(`plans.features.${vm.feature}`)
            if (vm.limitKey) return vm.$t(`plans.limitTitles.${vm.limitKey}`)
            return ''
        },
        description(vm = this) {
            if (vm.isStudent) return vm.$t('plans.student_notice')
            if (vm.notice) return vm.notice
            if (vm.limitKey) {
                if (!vm.max) return vm.$t('plans.limit_none', { name: vm.$t(`plans.limits.${vm.limitKey}`) })
                return vm.$t('plans.limit_reached', { max: vm.max, name: vm.$t(`plans.limits.${vm.limitKey}`) })
            }
            return vm.$t('plans.available_from', { plan: vm.tierLabel(vm.minTier) })
        },
        showUpgrade(vm = this) {
            return vm.ownsSubscription && vm.paymentsEnabled
        },
        tutorials(vm = this) {
            if (vm.isStudent || !vm.feature) return []
            return tutorialsFrom(vm.tutorialsConfig, { feature: vm.feature, accountType: vm.accountType })
        },
        hasTutorials(vm = this) {
            return vm.tutorials.length > 0
        },
        title(vm = this) {
            if (vm.step === 1) return vm.featureLabel
            if (vm.selected) return localizedField(vm.selected, 'title', vm.$i18n.locale) || ''
            return vm.$t('common.tutorials')
        },
    },
    mounted() {
        const vm = this
        if (!vm.isStudent) vm.fetchTutorials()
        vm.$emit('title', vm.title)
    },
    methods: {
        ...mapActions(['fetchTutorials']),
        tierLabel(tier) {
            if (!tier) return ''
            return this.$t(`plans.${this.isCompanyPlan ? 'companyTiers' : 'tiers'}.${tier}`)
        },
        isIncluded(tier) {
            if (this.limitKey) return this.limitForTier(tier) > this.max
            if (!this.minTier) return true
            return TIERS.indexOf(tier) >= TIERS.indexOf(this.minTier)
        },
        limitForTier(tier) {
            return LIMITS[this.limitKey]?.[tier] ?? 0
        },
        openTutorials() {
            const vm = this
            if (vm.tutorials.length === 1) {
                vm.fromList = false
                vm.selected = vm.tutorials[0]
            } else {
                vm.fromList = true
                vm.selected = null
            }
            vm.step = 2
        },
        onSelect(tutorial) {
            this.selected = tutorial
        },
        back() {
            const vm = this
            if (vm.selected && vm.fromList) return vm.selected = null
            vm.selected = null
            vm.step = 1
        },
        onUpgrade() {
            this.$refs.selectPlan?.show()
        },
        reset() {
            const vm = this
            vm.$refs.tutorialList?.pause()
            vm.step = 1
            vm.selected = null
            vm.fromList = false
        },
    },
    watch: {
        title(val) {
            this.$emit('title', val)
        },
        step(val) {
            this.$emit('step', val)
        },
    },
}
</script>

<style scoped>
.plan-required--inline {
    max-width: 460px;
    margin: 40px auto;
    padding: 24px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
}

.plan-required--inline .plan-req-title {
    font-size: var(--fs-title);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    margin-bottom: 10px;
}

.plan-req-desc {
    font-size: 14px;
    color: var(--v-txtSecondary-base);
    line-height: 1.5;
}

.plan-req-row {
    display: flex;
    gap: 8px;
    margin-top: 16px;
}

.plan-req-pill {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 7px;
    padding: 14px 6px;
    border-radius: 10px;
    border: 1.5px solid var(--v-outline-base);
    background: var(--v-surface2-base);
}

.plan-req-pill .v-icon {
    color: var(--v-txtMuted-base);
    opacity: 0.6;
}

.plan-req-pill.included {
    border-color: rgba(0, 153, 102, 0.4);
    background: rgba(0, 153, 102, 0.08);
}

.plan-req-pill.included .v-icon {
    color: var(--v-success-base);
    opacity: 1;
}

.plan-req-pill-name {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--v-txtMuted-base);
    text-align: center;
}

.plan-req-pill.included .plan-req-pill-name {
    color: var(--v-success-base);
}

.plan-req-pill-limit {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.plan-req-tutorial {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 18px;
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: color 0.15s;
}

.plan-req-tutorial:hover {
    color: var(--v-primary-base);
}

.theme--light .plan-req-tutorial:hover {
    color: var(--v-accent-base);
}

.plan-req-tutorial-icon {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 1px solid var(--v-outline2-base);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.plan-req-tutorial-icon .v-icon {
    color: var(--v-txtMuted-base);
}

.plan-req-tutorial:hover .plan-req-tutorial-icon .v-icon {
    color: inherit;
}

.plan-req-cta {
    margin-top: 20px;
    border-radius: 8px !important;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
}
</style>
